import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronDown, ChevronUp } from 'lucide-react';

const faqs = [
  {
    question: 'How do I get started with EzyAbroad as a consultancy?',
    answer: 'Simply click "Create Free Account" on our homepage, fill in your consultancy details, and you will have instant access to a 14-day free trial of our Pro plan. No credit card required.',
  },
  {
    question: 'Is EzyAbroad free for students?',
    answer: 'Yes! The EzyAbroad app is completely free for students. You can search consultancies, book appointments, chat with AI, and access country guides at no cost.',
  },
  {
    question: 'Can I switch between pricing plans?',
    answer: 'Absolutely. You can upgrade or downgrade your plan at any time from your dashboard. Changes take effect at the start of your next billing cycle.',
  },
  {
    question: 'How does the AI study advisor work?',
    answer: 'Our AI is trained on thousands of study abroad scenarios. Students can ask questions about visas, universities, courses, scholarships, and more — and get instant, accurate responses.',
  },
  {
    question: 'What payment methods do you accept?',
    answer: 'We accept all major credit and debit cards, UPI, net banking, and Razorpay for Indian consultancies. International payments are supported via Stripe.',
  },
  {
    question: 'How do I add counselors to my account?',
    answer: 'From your dashboard, go to Team Management and click "Add Counselor." Enter their email and they will receive an invitation to join your consultancy on EzyAbroad.',
  },
  {
    question: 'Is my student data secure?',
    answer: 'Yes. We use industry-standard encryption, regular security audits, and comply with data protection regulations. Your data is never sold to third parties.',
  },
  {
    question: 'How can I contact support?',
    answer: 'You can reach us via email at hello@ezyabroad.com, WhatsApp at +91 98765 43210, or through the contact form on our website. We typically respond within 24 hours.',
  },
];

export default function Support() {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFaqs = faqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-gray-100">
        <div className="w-full px-4 md:px-8 lg:px-12 py-4">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <button onClick={() => navigate('/')} className="text-xl font-bold text-navy-600 cursor-pointer">
              EzyAbroad
            </button>
            <button
              onClick={() => navigate('/')}
              className="text-sm text-gray-500 hover:text-navy-600 cursor-pointer"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      </header>

      <main className="w-full px-4 md:px-8 lg:px-12 py-12 md:py-16">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold tracking-widest text-gold-600 uppercase">Help & Support</span>
            <h1 className="mt-3 text-3xl md:text-4xl font-bold text-navy-600">
              How Can We Help You?
            </h1>
            <p className="mt-3 text-base text-gray-500">
              Find answers to common questions or reach out to our team.
            </p>
          </div>

          {/* Search */}
          <div className="relative mb-10">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for answers..."
              className="w-full pl-12 pr-4 py-3.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400 transition-colors"
            />
          </div>

          {/* Contact Cards */}
          <div className="grid sm:grid-cols-3 gap-4 mb-10">
            <div className="bg-gray-50 rounded-lg p-5 text-center">
              <div className="w-10 h-10 bg-gold-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-mail-line text-gold-600" />
              </div>
              <div className="text-sm font-medium text-navy-600">Email Us</div>
              <div className="text-xs text-gray-500 mt-1">hello@ezyabroad.com</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-5 text-center">
              <div className="w-10 h-10 bg-gold-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-whatsapp-line text-gold-600" />
              </div>
              <div className="text-sm font-medium text-navy-600">WhatsApp</div>
              <div className="text-xs text-gray-500 mt-1">+91 98765 43210</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-5 text-center">
              <div className="w-10 h-10 bg-gold-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <i className="ri-time-line text-gold-600" />
              </div>
              <div className="text-sm font-medium text-navy-600">Response Time</div>
              <div className="text-xs text-gray-500 mt-1">Within 24 hours</div>
            </div>
          </div>

          {/* FAQs */}
          <h2 className="text-xl font-bold text-navy-600 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {filteredFaqs.map((faq, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full flex items-center justify-between p-4 text-left cursor-pointer"
                >
                  <span className="text-sm font-medium text-navy-600 pr-4">{faq.question}</span>
                  {openIndex === index ? (
                    <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openIndex === index && (
                  <div className="px-4 pb-4">
                    <p className="text-sm text-gray-500 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {filteredFaqs.length === 0 && (
            <div className="text-center py-10">
              <i className="ri-question-line text-4xl text-gray-300" />
              <p className="mt-3 text-sm text-gray-500">No results found. Try a different search term.</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
